import { type RefObject } from 'react';

interface ScrollableVisibility {
  scrollableRect: DOMRect;
  scrollablePadding: number;
  scrollableScroll: number;
  children: ScrollableChild[];
}

interface ScrollableChild {
  x: number;
  width: number;
  visibility: number;
  element: HTMLElement;
}

export const getScrollableVisibility = (
  scrollable: RefObject<HTMLDivElement>,
): ScrollableVisibility | null => {
  if (
    !(
      scrollable &&
      scrollable.current &&
      scrollable.current.children.length !== 0
    )
  ) {
    return null;
  }

  const scrollableRect = scrollable.current.getBoundingClientRect();

  // 1st pass : compute real children bounding boxes

  const children = Array.from(scrollable.current.children).reduce<
    ScrollableChild[]
  >((previousValue: ScrollableChild[], currentElement: Element) => {
    const rect = (currentElement as HTMLElement).getBoundingClientRect();
    if (rect.width > 0) {
      return [
        ...previousValue,
        {
          x: Math.round(rect.x - scrollableRect.x),
          width: Math.ceil(rect.width),
          visibility: 1,
          element: currentElement as HTMLElement,
        },
      ];
    }

    return previousValue;
  }, []);

  if (children.length === 0) {
    return null;
  }

  // 2nd pass : update visibility with potential "bleeding" padding

  const scrollableScroll = getScroll(scrollable);
  const scrollablePadding = scrollableScroll + children[0].x || 0;

  children.forEach((item) => {
    let visibility = 1;

    const maxVisibleWidth = Math.min(
      item.width,
      scrollableRect.width - 2 * scrollablePadding,
    );

    if (item.x > scrollableRect.width - scrollablePadding) {
      visibility = 0;
    } else if (item.x < scrollablePadding) {
      visibility = Math.max(
        0,
        (item.x - scrollablePadding + item.width) / maxVisibleWidth,
      );
    } else if (item.x + item.width > scrollableRect.width - scrollablePadding) {
      visibility = Math.max(
        0,
        (scrollableRect.width - scrollablePadding - item.x) / maxVisibleWidth,
      );
    }

    item.visibility = visibility;
  });

  return {
    scrollableRect,
    scrollablePadding,
    scrollableScroll,
    children,
  };
};

/**
 * Get scroll value
 */
export function getScroll(scrollable: RefObject<HTMLDivElement>): number {
  return scrollable && scrollable.current
    ? Math.round(scrollable.current.scrollLeft)
    : 0;
}

/**
 * Get scroll max value
 */
export function getScrollMax(scrollable: RefObject<HTMLDivElement>): number {
  return scrollable && scrollable.current
    ? Math.round(
        scrollable.current.scrollWidth - scrollable.current.clientWidth,
      )
    : 0;
}
